import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useUserAccess } from '../context/UserAccessContext';
import { LoadingSpinner } from './LoadingSpinner';
import { AccessDenied } from './AccessDenied';
import { matchRouteAccess, isAdminArea, BASIC_ACCESS_MENU_ID } from '../lib/routeAccess';
import { isKioskOnlyMenus, KIOSK_PATH } from '../utils/kiosk';

/**
 * PortalGuruAccessGate — menegakkan Broken Access Control di level routing.
 *
 * Dibungkus di sekeliling seluruh rute Portal Guru (lihat PortalGuruRoutes.tsx).
 * Mencocokkan pathname dengan lib/routeAccess.ts, lalu:
 *   - Super Admin          → selalu diizinkan (bypass).
 *   - Rule `superAdminOnly` → hanya Super Admin (default-deny).
 *   - Rule `menuIds`        → user cukup punya SALAH SATU menu ID (OR).
 *   - Tanpa rule, area /admin → hanya Super Admin.
 *   - Tanpa rule lainnya     → butuh akses dasar `portal-guru`.
 *
 * Ditolak → render <AccessDenied /> (403 + countdown auto-redirect), TIDAK merender
 * halaman sama sekali.
 */
export const PortalGuruAccessGate: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { authUser, loading: authLoading } = useAuth();
  const { isSuperAdmin, checkUserAccess, userPermissions, loading: accessLoading } = useUserAccess();
  const location = useLocation();

  if (authLoading || accessLoading) {
    return <LoadingSpinner />;
  }

  if (!authUser) {
    return <Navigate to="/" replace />;
  }

  const pathname = location.pathname;
  const email = authUser.email || '';

  // Kiosk Presensi (docs/PANDUAN_KIOSK_PRESENSI.md): akun yang hanya diberi menu kiosk
  // tidak punya Portal Guru, jadi selalu dialihkan ke /kiosk. Akun guru/admin tidak.
  if (!isSuperAdmin && (pathname === '/portal-guru' || pathname === '/portal-guru/')) {
    const allowedMenus = userPermissions.find(p => p.email === email)?.allowedMenus || [];
    if (isKioskOnlyMenus(allowedMenus)) {
      return <Navigate to={KIOSK_PATH} replace />;
    }
  }

  if (isSuperAdmin) {
    return <>{children}</>;
  }

  const rule = matchRouteAccess(pathname);

  let allowed = false;

  if (rule) {
    if (rule.superAdminOnly) {
      allowed = false;
    } else if (rule.menuIds && rule.menuIds.length > 0) {
      allowed = rule.menuIds.some((menuId) => checkUserAccess(email, menuId));
    } else {
      allowed = false;
    }
  } else if (isAdminArea(pathname)) {
    // Area /admin tanpa rule spesifik → hanya Super Admin.
    allowed = false;
  } else {
    // Rute umum Portal Guru → butuh akses dasar `portal-guru`.
    allowed = checkUserAccess(email, BASIC_ACCESS_MENU_ID);
  }

  if (!allowed) {
    return <AccessDenied />;
  }

  return <>{children}</>;
};

export default PortalGuruAccessGate;
